import type { InviteContact } from './types'

/**
 * Reading "whatever the organizer typed" as a party.
 *
 * A line may be an EMAIL OR A PHONE — the people a member golfs with are mostly
 * not members, and the organizer has whichever they have. Anything that is
 * neither is REJECTED rather than dropped: an invitee who silently vanishes
 * from the party is worse than an error, and the server (Golf::Invites) makes
 * the same choice for the same reason.
 *
 * Shared by the two screens that compose a party — the outings desk, where the
 * list is typed from scratch, and The Record, where it arrives pre-filled from
 * a past outing and is then edited.
 */

/** Policy, and it lives on the server too (OutingsController::MAX_INVITES). */
export const MAX_INVITES = 7

const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const PHONE_SHAPE = /^\+?[\d\s().-]{7,}$/

/** What one typed line is, or null when it is neither. */
export function contactFromLine(line: string): InviteContact | null {
  const value = line.trim()
  if (!value) return null
  if (value.includes('@')) return EMAIL_SHAPE.test(value) ? { email: value } : null
  return PHONE_SHAPE.test(value) ? { phone: value } : null
}

export interface ParsedContacts {
  contacts: InviteContact[]
  error: string | null
}

/** One contact per line, capped, with the first bad line named. */
export function parseInviteContacts(raw: string): ParsedContacts {
  const lines = raw
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
  if (lines.length > MAX_INVITES) {
    return {
      contacts: [],
      error: `That's ${lines.length} — an outing takes at most ${MAX_INVITES} invitations.`,
    }
  }
  const contacts: InviteContact[] = []
  for (const line of lines) {
    const contact = contactFromLine(line)
    if (!contact) {
      return {
        contacts: [],
        error: line.includes('@')
          ? `"${line}" doesn't look like an email address.`
          : `"${line}" is neither an email address nor a phone number.`,
      }
    }
    contacts.push(contact)
  }
  return { contacts, error: null }
}
